import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import anime from 'animejs';

class Component extends React.PureComponent {
  static displayName = 'Background';

  static propTypes = {
    theme: PropTypes.object.isRequired,
    classes: PropTypes.object.isRequired,
    energy: PropTypes.object.isRequired,
    audio: PropTypes.object.isRequired,
    sounds: PropTypes.object.isRequired,
    className: PropTypes.any,
    children: PropTypes.any,
    initialMaxDuration: PropTypes.number,
    onEnter: PropTypes.func,
    onExit: PropTypes.func
  };

  static defaultProps = {
    initialMaxDuration: 2000
  };

  constructor () {
    super(...arguments);

    // Black canvas properties
    this.canvas = null;
    this.ctx = null;
    this.animId = null;
    this.lastTime = 0;
    this.lastTwinkleTime = 0;

    this.width = 0;
    this.height = 0;
    this.cellSize = 14;
    this.cols = 0;
    this.rows = 0;
    this.cells = null;
    this.ripples = [];

    // Frequency analyzer data
    this.freqData = new Uint8Array(128);

    // Asenkron ve bağımsız ömürlü aktif kare kümeleri
    this.activeClusters = [];
    this.nextClusterSpawnTime = 0;

    this.pointer = { x: -9999, y: -9999, active: false };
    this.opacityObj = { value: 0 };

    if (this.props.energy && this.props.energy.updateDuration) {
      this.props.energy.updateDuration({ enter: 1000, exit: 600 });
    }
  }

  componentDidMount () {
    this.initCanvas();
    this.startLoop();

    window.addEventListener('resize', this.onResize, { passive: true });
    window.addEventListener('pointerdown', this.onPointerDown, { passive: true });
    window.addEventListener('pointermove', this.onPointerMove, { passive: true });
    window.addEventListener('pointerleave', this.onPointerLeave, { passive: true });

    if (this.props.energy && this.props.energy.entered) {
      this.opacityObj.value = 1;
    }
  }

  componentWillUnmount () {
    const { sounds } = this.props;

    this.stopLoop();

    if (sounds && sounds.start) {
      sounds.start.stop();
    }

    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerleave', this.onPointerLeave);
  }

  onResize = () => {
    this.initCanvas();
  }

  // ==========================================
  // BLACK CANVAS BACKGROUND METHODS
  // ==========================================

  initCanvas () {
    if (!this.canvas) return;

    const width = window.innerWidth || 1000;
    const height = window.innerHeight || 800;
    const dpr = window.devicePixelRatio || 1;

    this.width = width;
    this.height = height;
    this.cellSize = width <= 600 ? 11 : 14;

    this.cols = Math.ceil(width / this.cellSize) + 1;
    this.rows = Math.ceil(height / this.cellSize) + 1;
    const totalCells = this.cols * this.rows;

    const oldCells = this.cells;
    this.cells = new Float32Array(totalCells);
    if (oldCells) {
      for (let i = 0; i < Math.min(oldCells.length, totalCells); i++) {
        this.cells[i] = oldCells[i];
      }
    }

    this.canvas.width = Math.floor(width * dpr);
    this.canvas.height = Math.floor(height * dpr);
    this.canvas.style.width = width + 'px';
    this.canvas.style.height = height + 'px';

    this.ctx = this.canvas.getContext('2d');
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  addRipple (x, y, initialPower = 1.0, initialSpeed = 700) {
    const maxRadius = Math.hypot(
      Math.max(x, this.width - x),
      Math.max(y, this.height - y)
    ) * 1.1;

    this.ripples.push({
      x,
      y,
      radius: 0,
      maxRadius: Math.max(400, maxRadius),
      speed: initialSpeed,
      power: initialPower
    });

    if (this.cells && this.cols > 0 && this.cellSize > 0) {
      const col = Math.floor(x / this.cellSize);
      const row = Math.floor(y / this.cellSize);
      if (col >= 0 && col < this.cols && row >= 0 && row < this.rows) {
        const idx = row * this.cols + col;
        this.cells[idx] = 1.0;
      }
    }
  }

  onPointerDown = (event) => {
    const x = event.clientX;
    const y = event.clientY;

    this.pointer.x = x;
    this.pointer.y = y;
    this.pointer.active = true;

    const { sounds, audio } = this.props;
    if (!audio || !audio.silent) {
      if (sounds && sounds.hover) {
        sounds.hover.play();
      }
    }

    this.addRipple(x, y, 1.0, 750);
  }

  onPointerMove = (event) => {
    this.pointer.x = event.clientX;
    this.pointer.y = event.clientY;
    this.pointer.active = true;

    if (this.cells && this.cols > 0 && this.cellSize > 0) {
      const col = Math.floor(event.clientX / this.cellSize);
      const row = Math.floor(event.clientY / this.cellSize);

      for (let r = Math.max(0, row - 2); r <= Math.min(this.rows - 1, row + 2); r++) {
        for (let c = Math.max(0, col - 2); c <= Math.min(this.cols - 1, col + 2); c++) {
          const dist = Math.hypot(c - col, r - row);
          if (dist <= 2.2) {
            const idx = r * this.cols + c;
            const boost = Math.max(0.18, 0.6 * (1 - dist / 2.5));
            if (this.cells[idx] < boost) {
              this.cells[idx] = boost;
            }
          }
        }
      }
    }
  }

  onPointerLeave = () => {
    this.pointer.active = false;
  }

  startLoop () {
    this.lastTime = performance.now();
    const render = (time) => {
      const dt = Math.min(0.1, (time - this.lastTime) / 1000);
      this.lastTime = time;

      this.update(time, dt);
      this.draw(time);

      this.animId = requestAnimationFrame(render);
    };

    this.animId = requestAnimationFrame(render);
  }

  stopLoop () {
    if (this.animId) {
      cancelAnimationFrame(this.animId);
      this.animId = null;
    }
  }

  spawnEqualizerColumn (col, heightRatio, power) {
    const activeRowCount = Math.round(heightRatio * (this.rows - 2));
    const startRow = this.rows - 1;

    for (let i = 0; i < activeRowCount; i++) {
      const row = startRow - i;
      if (row >= 0 && row < this.rows) {
        const idx = row * this.cols + col;
        const cellIntensity = Math.min(1.0, power * (1.0 - (i / (activeRowCount + 1)) * 0.25));
        if (this.cells[idx] < cellIntensity) {
          this.cells[idx] = cellIntensity;
        }
      }
    }
  }

  generateClusterCells (targetSize) {
    if (this.cols <= 6 || this.rows <= 6) return [];
    const startCol = Math.floor(Math.random() * (this.cols - 6)) + 3;
    const startRow = Math.floor(Math.random() * (this.rows - 6)) + 3;

    const clusterCells = [{ col: startCol, row: startRow }];
    const directions = [
      { dc: 1, dr: 0 },
      { dc: -1, dr: 0 },
      { dc: 0, dr: 1 },
      { dc: 0, dr: -1 }
    ];

    let attempts = 0;
    while (clusterCells.length < targetSize && attempts < 24) {
      attempts++;
      const current = clusterCells[Math.floor(Math.random() * clusterCells.length)];
      const dir = directions[Math.floor(Math.random() * directions.length)];
      const nextCol = current.col + dir.dc;
      const nextRow = current.row + dir.dr;
      if (
        nextCol >= 0 && nextCol < this.cols &&
        nextRow >= 0 && nextRow < this.rows &&
        !clusterCells.some(c => c.col === nextCol && c.row === nextRow)
      ) {
        clusterCells.push({ col: nextCol, row: nextRow });
      }
    }
    return clusterCells;
  }

  update (time, dt) {
    if (!this.cells || this.cols === 0 || this.rows === 0) return;

    let hasMusicAudio = false;
    if (typeof window !== 'undefined' && window.__bgAudioAnalyser && !window.__bgAudioIsMuted) {
      try {
        window.__bgAudioAnalyser.getByteFrequencyData(this.freqData);
        let sum = 0;
        for (let i = 0; i < 32; i++) {
          sum += this.freqData[i];
        }
        if (sum > 60) {
          hasMusicAudio = true;
        }
      } catch (err) {
        hasMusicAudio = false;
      }
    }

    if (hasMusicAudio) {
      const numCols = Math.max(1, this.cols);

      for (let col = 0; col < this.cols; col++) {
        const normCol = col / (numCols - 1 || 1);
        // Left is bass (bins 1..16), right is treble (bins 17..60)
        const binIndex = Math.min(60, Math.floor(1 + normCol * 54));
        const val = this.freqData[binIndex] || 0;

        if (val > 14) {
          const normVal = val / 255;
          const heightRatio = Math.min(0.52, Math.max(0.05, Math.pow(normVal, 0.95) * 0.65));
          const power = Math.min(1.0, 0.42 + normVal * 0.55);
          this.spawnEqualizerColumn(col, heightRatio, power);
        }
      }
    }

    // Doğal, asenkron ve organik aralıklarla küme doğumu:
    // Bazısı farklı zamanda, bazısı aynı anda, kalabalık boğucu olmadan
    if (time >= this.nextClusterSpawnTime) {
      const maxActive = hasMusicAudio ? 10 : 8;
      if (this.activeClusters.length < maxActive) {
        // %30 ihtimalle 2 küme birden (aynı anda), %70 tek küme
        const spawnCount = Math.random() < 0.3 ? 2 : 1;
        const clusterSizes = [2, 3, 3, 4, 4, 5, 6];

        for (let s = 0; s < spawnCount; s++) {
          const size = clusterSizes[Math.floor(Math.random() * clusterSizes.length)];
          const cells = this.generateClusterCells(size);
          if (cells.length > 0) {
            this.activeClusters.push({
              cells,
              startTime: time,
              duration: Math.floor(750 + Math.random() * 450), // 750ms - 1200ms kalacak bir süre
              peakIntensity: 0.55 + Math.random() * 0.42
            });
          }
        }
      }

      // Asenkron aralık: bazen hızlı ardışık (70-130ms), bazen nefesli (160-340ms)
      const isQuick = Math.random() < 0.35;
      const delay = isQuick
        ? Math.floor(Math.random() * 60 + 70)
        : Math.floor(Math.random() * 180 + 160);
      this.nextClusterSpawnTime = time + (hasMusicAudio ? Math.floor(delay * 0.75) : delay);
    }

    for (let i = this.ripples.length - 1; i >= 0; i--) {
      const rip = this.ripples[i];
      rip.radius += rip.speed * dt;

      if (rip.radius > rip.maxRadius) {
        this.ripples.splice(i, 1);
        continue;
      }

      const currentRadius = rip.radius;
      const thickness = this.cellSize * 1.5;
      const innerR = Math.max(0, currentRadius - thickness);
      const outerR = currentRadius + thickness;
      const ripPower = rip.power * Math.max(0, 1 - currentRadius / rip.maxRadius);

      if (ripPower <= 0.01) {
        this.ripples.splice(i, 1);
        continue;
      }

      const minCol = Math.max(0, Math.floor((rip.x - outerR) / this.cellSize));
      const maxCol = Math.min(this.cols - 1, Math.ceil((rip.x + outerR) / this.cellSize));
      const minRow = Math.max(0, Math.floor((rip.y - outerR) / this.cellSize));
      const maxRow = Math.min(this.rows - 1, Math.ceil((rip.y + outerR) / this.cellSize));

      for (let r = minRow; r <= maxRow; r++) {
        const cellCenterY = r * this.cellSize + this.cellSize / 2;
        const dy = cellCenterY - rip.y;
        for (let c = minCol; c <= maxCol; c++) {
          const cellCenterX = c * this.cellSize + this.cellSize / 2;
          const dx = cellCenterX - rip.x;
          const dist = Math.hypot(dx, dy);

          if (dist >= innerR && dist <= outerR) {
            const wavePos = 1 - Math.abs(dist - currentRadius) / thickness;
            const boost = wavePos * ripPower * 0.95;
            const idx = r * this.cols + c;
            if (this.cells[idx] < boost) {
              this.cells[idx] = boost;
            }
          }
        }
      }
    }

    // Dalgalar ve tıklamalar için yumuşak decay
    const decayRate = hasMusicAudio ? 0.84 : 0.87;
    const decayFactor = Math.pow(decayRate, dt * 60);
    const totalCells = this.cols * this.rows;
    for (let i = 0; i < totalCells; i++) {
      if (this.cells[i] > 0.005) {
        this.cells[i] *= decayFactor;
        if (this.cells[i] < 0.005) {
          this.cells[i] = 0;
        }
      }
    }

    // Aktif kare kümeleri: her biri kendi bağımsız ömründe kalır,
    // biri ekranda kalırken bir yandan başkası yanıp söner
    for (let i = this.activeClusters.length - 1; i >= 0; i--) {
      const cluster = this.activeClusters[i];
      const elapsed = time - cluster.startTime;

      if (elapsed >= cluster.duration) {
        this.activeClusters.splice(i, 1);
        continue;
      }

      const p = elapsed / cluster.duration;
      let alpha = 0;
      if (p < 0.16) {
        // Yumuşak açılış
        alpha = (p / 0.16) * cluster.peakIntensity;
      } else if (p < 0.52) {
        // Bir süre belirgin ve parlak kalma
        alpha = cluster.peakIntensity;
      } else {
        // Yumuşak ve zarif sönüş
        alpha = ((1 - p) / 0.48) * cluster.peakIntensity;
      }

      for (let ci = 0; ci < cluster.cells.length; ci++) {
        const { col, row } = cluster.cells[ci];
        if (col >= 0 && col < this.cols && row >= 0 && row < this.rows) {
          const idx = row * this.cols + col;
          if (this.cells[idx] < alpha) {
            this.cells[idx] = alpha;
          }
        }
      }
    }
  }

  draw (time = 0) {
    const ctx = this.ctx;
    if (!ctx) return;

    const width = this.width;
    const height = this.height;
    const cellSize = this.cellSize;
    const cols = this.cols;
    const rows = this.rows;
    const compOpacity = this.opacityObj.value;

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, width, height);

    if (compOpacity <= 0) return;

    // Smoothly drifting base hue transitioning across Omarchy palettes (teal -> cyan -> slate -> emerald -> purple)
    const baseHue = (168 + (time * 0.007)) % 360;
    const squareSize = Math.max(1, cellSize - 2);

    // Aktif parlayan kareler (Omarchy dither pixel tiles)
    const totalCells = cols * rows;
    for (let i = 0; i < totalCells; i++) {
      const val = this.cells[i];
      if (val <= 0.005) continue;

      const alpha = Math.min(1, val) * compOpacity;
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = col * cellSize;
      const y = row * cellSize;

      const hueVariation = ((col * 17 + row * 29) % 32) - 16;
      const cellHue = (baseHue + hueVariation + 360) % 360;
      const sat = 45 + ((col * 7 + row * 13) % 25);
      const light = 50 + ((col * 11 + row * 19) % 20);

      ctx.fillStyle = `hsla(${cellHue.toFixed(1)}, ${sat}%, ${light}%, ${(alpha * 0.9).toFixed(3)})`;
      ctx.fillRect(x + 1, y + 1, squareSize, squareSize);

      if (alpha > 0.45) {
        ctx.fillStyle = `hsla(${cellHue.toFixed(1)}, 35%, 85%, ${(alpha * 0.55).toFixed(3)})`;
        ctx.fillRect(x + 2, y + 2, Math.max(1, squareSize - 2), Math.max(1, squareSize - 2));
      }
    }
  }

  enterBlack () {
    const { sounds, onEnter } = this.props;

    anime.remove(this.opacityObj);
    anime({
      targets: this.opacityObj,
      value: 1,
      duration: 1000,
      easing: 'easeOutCubic',
      complete: () => {
        if (onEnter) onEnter();
      }
    });

    const cx = this.width / 2 || (typeof window !== 'undefined' ? window.innerWidth / 2 : 500);
    const cy = this.height / 2 || (typeof window !== 'undefined' ? window.innerHeight / 2 : 400);
    this.addRipple(cx, cy, 0.85, 650);

    if (sounds && sounds.start) {
      sounds.start.play();
    }
  }

  exitBlack () {
    const { sounds, onExit } = this.props;

    anime.remove(this.opacityObj);
    anime({
      targets: this.opacityObj,
      value: 0,
      duration: 600,
      easing: 'easeInCubic',
      complete: () => {
        if (onExit) onExit();
      }
    });

    if (sounds && sounds.fade) {
      sounds.fade.play();
    }
  }

  enter () {
    this.enterBlack();
  }

  exit () {
    this.exitBlack();
  }

  render () {
    const {
      classes,
      className,
      children,
      theme,
      energy,
      audio,
      sounds,
      initialMaxDuration,
      onEnter,
      onExit,
      ...etc
    } = this.props;

    return (
      <div className={cx(classes.root, className)} {...etc}>
        <canvas
          ref={ref => (this.canvas = ref)}
          className={classes.canvas}
        />
        <div className={classes.content}>{children}</div>
      </div>
    );
  }
}

export { Component };
